import { connect } from 'react-redux';
import { useState, useEffect, useRef } from 'react';
import { SET_DARK_MODE } from '../actions/types';
import { siteboardAction } from '../actions/defaultActions';

// icon imports
import { Sun, Moon } from 'lucide-react';
import MoonIcon from '../icons/moonIcon';
import SunIcon from '../icons/sunIcon';

const DarkModeToggle = ({
  _userPreferences,
  siteboardAction,
  onToggle,
  moonColor = '#b9a6ff',
  small = false,
}) => {
  const isDark = _userPreferences.darkMode === true;

  const dimensions = small
    ? { width: 50, height: 28, thumb: 22, inset: 3 }
    : { width: 88, height: 38, thumb: 30, inset: 4 };

  const { width, height, thumb, inset } = dimensions;

  const thumbPosition = isDark
    ? width - thumb - inset
    : inset;

  const handleToggle = () => {
    const newDarkMode = !isDark;

    siteboardAction(SET_DARK_MODE, {
      enabled: newDarkMode,
    });

    if (typeof onToggle === 'function') {
      onToggle(newDarkMode);
    }
  };

  return (
    <button
      type="button"
      role="switch"
      aria-checked={isDark}
      aria-label="Dark mode"
      title={`Switch to ${isDark ? 'light' : 'dark'} theme`}
      onClick={handleToggle}
      className="
        group relative inline-flex shrink-0
        items-center justify-center
        rounded-full
        select-none
        transition-transform duration-200
        hover:scale-[1.03]
        active:scale-[0.97]
        focus-visible:outline-none
        focus-visible:ring-2
        focus-visible:ring-[#8060ff]
        focus-visible:ring-offset-2
        focus-visible:ring-offset-[#222]
      "
    >
      {/* Track */}
      <div
        className={`
          relative overflow-hidden rounded-full
          border transition-colors duration-300
          ${
            isDark
              ? 'border-[#555] bg-[#282336]'
              : 'border-[#b6a384] bg-[#fff]'
          }
        `}
        style={{
          width,
          height,
        }}
      >
        {/* Labels for regular-sized toggle */}
        {!small && (
          <>
            {/* Dark label */}
            <span
              className={`
                pointer-events-none absolute
                left-[11px] top-1/2
                -translate-y-1/2
                text-xs font-bold tracking-wide
                transition-all duration-300
                ${
                  isDark
                    ? 'translate-x-0 text-white/90 opacity-100'
                    : '-translate-x-2 text-[#d3c7ff] opacity-0'
                }
              `}
            >
              Dark
            </span>

            {/* Light label */}
            <span
              className={`
                pointer-events-none absolute
                right-[10px] top-1/2
                -translate-y-1/2
                text-xs font-bold tracking-wide
                transition-all duration-300
                ${
                  !isDark
                    ? 'translate-x-0 text-[#59492f] opacity-100'
                    : 'translate-x-2 text-[#59492f] opacity-0'
                }
              `}
            >
              Light
            </span>
          </>
        )}

        {/* Sliding thumb */}
        <span
          className={`
            absolute top-1/2
            flex items-center justify-center
            rounded-full border
            shadow-[0_2px_6px_rgba(0,0,0,0.3)]
            transition-[transform,background-color,border-color]
            duration-300
            ease-[cubic-bezier(0.34,1.35,0.64,1)]
            ${
              isDark
                ? 'border-[#8d75ff] bg-[#191625]'
                : 'border-[#d99b2f] bg-[#f9a928]'
            }
          `}
          style={{
            width: thumb,
            height: thumb,
            transform: `translateX(${thumbPosition}px) translateY(-50%)`,
          }}
        >
          {/* Sun */}
          <Sun
            size={small ? 15 : 18}
            strokeWidth={2.3}
            className={`
              absolute text-[#573500]
              transition-all duration-300
              ${
                isDark
                  ? '-rotate-90 scale-50 opacity-0'
                  : 'rotate-0 scale-100 opacity-100'
              }
            `}
          />

          {/* Moon */}
          <Moon
            size={small ? 15 : 18}
            strokeWidth={2.2}
            style={{ color: moonColor }}
            className={`
              absolute transition-all duration-300
              ${
                isDark
                  ? 'rotate-0 scale-100 opacity-100'
                  : 'rotate-90 scale-50 opacity-0'
              }
            `}
          />
        </span>
      </div>
    </button>
  );
};

const mapStateToProps = (state) => ({
  _userPreferences: state.serverSynced.userPreferences,
});

export default connect(
  mapStateToProps,
  { siteboardAction }
)(DarkModeToggle);

const DarkModeToggleOld = ({ _userPreferences, siteboardAction, onToggle, moonColor }) => {
  return (
   <button 
     className='group flex items-center gap-x-2'
   title={`Switch to ${_userPreferences.darkMode === true ? 'light' : 'dark'} theme`}
     onClick={() => { 
       siteboardAction(SET_DARK_MODE, { enabled: _userPreferences.darkMode !== true }); 
       try { onToggle(_userPreferences.darkMode !== true); } catch(error) {}
     }}
   >
     <div className={`relative w-[26px] h-[14px] rounded-[20px] ${_userPreferences.darkMode === true ? 'bg-[#5f4cfe]' : 'bg-[#eaedf0]'}`}>
       <div className={`absolute flex justify-center items-center transition-all duration-100 top-1/2 left-0 translate-x-[-50%] translate-y-[-50%] w-[22px] h-[22px] rounded-full  ${_userPreferences.darkMode === true ? 'left-[26px] bg-[#14171d] border border-[#5f4cfe]' : 'bg-white drop-shadow-[0_3px_3px_rgba(80,80,80,1)]'}`}> 
         {_userPreferences.darkMode === true ? <MoonIcon size='16px' color='#5f4cfe' /> : <SunIcon size='16px' color='#fcaf4f' />}
       </div>
     </div>
   </button>
  );
};


// <button 
//   className='group w-[26px] flex justify-center items-center gap-x-2'
//   title={`Switch to ${_userPreferences.darkMode === true ? 'light' : 'dark'} mode`}
//   onClick={() => { 
//     siteboardAction(SET_DARK_MODE, { enabled: _userPreferences.darkMode !== true }); 
//     try { onToggle(_userPreferences.darkMode !== true); } catch(error) {}
//   }}
// >
//   {_userPreferences.darkMode === true ?  <SunIcon size='26' /> : <MoonIcon size='20' color={moonColor || '#eed29a'} /> }
// </button>

// <button 
//   className='group flex items-center gap-x-2'
// title={`Switch to ${_userPreferences.darkMode === true ? 'light' : 'dark'} mode`}
//   onClick={() => { 
//     siteboardAction(SET_DARK_MODE, { enabled: _userPreferences.darkMode !== true }); 
//     try { onToggle(_userPreferences.darkMode !== true); } catch(error) {}
//   }}
// >
//   <SunIcon size='26' />
//   <div className='relative w-[50px] h-[24px] rounded-[20px] bg-[#333]'>
//     <div className={`absolute transition-transform duration-100 top-[0px] left-0 w-[22px] h-[22px] bg-[#471cff] group-hover:bg-[#693eff] rounded-full ${_userPreferences.darkMode == true ? 'translate-x-[27px]' : ''}`}> </div>
//   </div>
//   <MoonIcon size='20' />
// </button>
